'use client';

import { logger } from '@/lib/logger';
import { useTokenApproval } from '@/hooks/useTokenApproval';
import { useAuth, useGetUser } from '@/hooks/useAuth';
import { Loader2, AlertCircle, CheckCircle2, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Alert, AlertDescription } from './ui/alert';
import { Buttons } from './Buttons';
import { useEffect, useState } from 'react';
import { useLoading } from '@/contexts/LoadingContext';

interface TokenApprovalGuardProps {
  children: React.ReactNode;
  /**
   * Optional callback when approval is successful
   */
  onApprovalSuccess?: () => void;
}

/**
 * TokenApprovalGuard - Ensures user has approved backend wallet for token transfers
 *
 * Flow:
 * 1. Checks user's token allowance for backend wallet
 * 2. If not approved: Shows modal with approve button
 * 3. User clicks approve → Triggers ERC20 approve() transaction
 * 4. After confirmation: Shows success and allows access
 *
 * This is REQUIRED for all transactions that use transferFrom() pattern:
 * - Membership purchases
 * - Bidding/Auction deposits
 * - Catalog purchases
 * - Voucher redemptions
 */
function TokenApprovalContent({ children, onApprovalSuccess }: TokenApprovalGuardProps) {
  const {
    isApproved,
    isCheckingAllowance,
    hasAllowanceError,
    allowanceError,
    isApproving,
    isFundingGas,
    isConfirming,
    isConfirmed,
    approvalError,
    fundingMessage,
    gasFundingData,
    isCheckingGasFunding,
    approve,
    refetchAllowance,
  } = useTokenApproval();

  const { logout } = useAuth();
  const { addLoadingStep, removeLoadingStep } = useLoading();
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  // Manage loading state with timeout
  useEffect(() => {
    if (isCheckingAllowance && !hasAllowanceError) {
      addLoadingStep('token-approval', 'Memeriksa status persetujuan token...');

      // Set timeout to prevent infinite loading (30 seconds)
      const timeout = setTimeout(() => {
        logger.error('[TokenApprovalGuard] Loading timeout after 30s');
        setLoadingTimeout(true);
        removeLoadingStep('token-approval');
      }, 30000);

      return () => {
        clearTimeout(timeout);
        removeLoadingStep('token-approval');
      };
    } else {
      removeLoadingStep('token-approval');
    }
  }, [isCheckingAllowance, hasAllowanceError, addLoadingStep, removeLoadingStep]);

  // Handle successful approval
  const handleApprovalSuccess = () => {
    refetchAllowance();
    if (onApprovalSuccess) {
      onApprovalSuccess();
    }
  };

  // Handle disconnect wallet and logout
  const handleDisconnect = async () => {
    try {
      await logout.mutateAsync();
    } catch (error) {
      logger.error('Failed to disconnect and logout:', error);
    }
  };

  // Auto-trigger callback when confirmed
  if (isConfirmed && onApprovalSuccess) {
    setTimeout(handleApprovalSuccess, 1000);
  }

  // Check if button should be disabled
  const isButtonDisabled =
    isApproving ||
    gasFundingData?.isBlacklisted === true ||
    gasFundingData?.hasMinimumBalance === false;

  logger.log('isButtonDisabled', isButtonDisabled);
  logger.log('gasFundingData', gasFundingData);

  // If loading timed out or has error, show error with retry
  if (loadingTimeout || hasAllowanceError) {
    return (
      <Dialog open={true} onOpenChange={() => {}}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>{loadingTimeout ? 'Connection Timeout' : 'Connection Error'}</DialogTitle>
            <DialogDescription>
              Unable to verify token approval status. Please check your internet connection and try
              again.
            </DialogDescription>
          </DialogHeader>

          <div className='space-y-4'>
            <Alert variant='destructive'>
              <AlertCircle className='h-4 w-4' />
              <AlertDescription>
                {loadingTimeout
                  ? 'Request timed out after 30 seconds. This might be due to network issues.'
                  : allowanceError || 'Failed to check token approval status.'}
              </AlertDescription>
            </Alert>

            <div className='flex gap-2'>
              <Button
                onClick={() => {
                  setLoadingTimeout(false);
                  refetchAllowance();
                }}
                className='flex-1'
              >
                Retry
              </Button>
              <Button
                variant='outline'
                onClick={handleDisconnect}
                disabled={logout.isPending}
                className='flex-1'
              >
                {logout.isPending ? (
                  <>
                    <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                    Disconnecting...
                  </>
                ) : (
                  <>
                    <LogOut className='w-4 h-4 mr-2' />
                    Logout
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  // If still checking, render children (loading screen is shown by LoadingContext)
  if (isCheckingAllowance) {
    return <>{children}</>;
  }

  // If user has already approved, render children
  if (isApproved) {
    return <>{children}</>;
  }

  // User needs to approve - show modal
  return (
    <Dialog open={!isApproved} onOpenChange={() => {}}>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Token Approval Required</DialogTitle>
          <DialogDescription>
            Before you can make transactions, you need to approve our platform to use your tokens.
            This is a one-time approval.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4'>
          {/* Explanation */}
          <div className='bg-blue-50 border border-blue-200 rounded-lg p-4'>
            <div className='flex gap-3'>
              <AlertCircle className='w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5' />
              <div className='text-sm text-blue-900'>
                <p className='font-medium mb-1'>Why is this needed?</p>
                <p className='mb-2'>
                  This one-time approval allows our platform to process token payments on your
                  behalf. We use a gas relay system where we pay all network fees for your
                  transactions.
                </p>

                {/* Security Status */}
                {isCheckingGasFunding ? (
                  <div className='text-xs bg-gray-50 border border-gray-200 rounded p-2 mb-2'>
                    <Loader2 className='w-3 h-3 inline mr-1 animate-spin' />
                    <span>Checking security and gas requirements...</span>
                  </div>
                ) : null}

                {/* Security Warnings */}
                {gasFundingData?.isBlacklisted ? (
                  <div className='text-xs bg-red-50 border border-red-200 rounded p-2 mb-2'>
                    <AlertCircle className='w-3 h-3 inline mr-1' />
                    <strong>Access Denied:</strong> {gasFundingData.blacklistReason}
                  </div>
                ) : gasFundingData?.alreadyFunded ? (
                  <div className='text-xs bg-amber-50 border border-amber-200 rounded p-2 mb-2'>
                    <AlertCircle className='w-3 h-3 inline mr-1' />
                    <strong>Already Funded:</strong> You received gas funding already. Please
                    approve within 10 minutes.
                  </div>
                ) : !gasFundingData?.hasMinimumBalance ? (
                  <div className='text-xs bg-amber-50 border border-amber-200 rounded p-2 mb-2'>
                    <AlertCircle className='w-3 h-3 inline mr-1' />
                    <strong>Minimum Balance Required:</strong> Your wallet must hold AsetQu (any
                    amount, even 0.000000000000000001) to proceed.
                  </div>
                ) : gasFundingData?.needsFunding && gasFundingData.minRequiredFormatted ? (
                  <div className='text-xs bg-green-50 border border-green-200 rounded p-2'>
                    <CheckCircle2 className='w-3 h-3 inline mr-1' />
                    <strong>Gas fee:</strong> {gasFundingData.minRequiredFormatted}
                    <br />
                    <span className='text-gray-600'>
                      • You'll sign a message to authorize funding
                      <br />
                      • After funding, you have 10 minutes to approve
                      <br />• This is a one-time process
                    </span>
                  </div>
                ) : null}
              </div>
            </div>
          </div>

          {/* Success message */}
          {isConfirmed && (
            <Alert className='bg-green-50 border-green-200'>
              <CheckCircle2 className='h-4 w-4 text-green-600' />
              <AlertDescription className='text-green-900'>
                Approval successful! You can now make transactions.
              </AlertDescription>
            </Alert>
          )}

          {/* Error message */}
          {approvalError && (
            <Alert variant='destructive'>
              <AlertCircle className='h-4 w-4' />
              <AlertDescription>{approvalError}</AlertDescription>
            </Alert>
          )}

          {/* Approval status */}
          {isApproving && !isConfirmed && (
            <div className='text-center py-4'>
              <Loader2 className='w-8 h-8 text-blue-600 animate-spin mx-auto mb-2' />
              <p className='text-sm text-gray-600'>
                {isFundingGas
                  ? fundingMessage || 'Preparing gas fee...'
                  : isConfirming
                    ? 'Waiting for confirmation...'
                    : fundingMessage || 'Waiting for wallet approval...'}
              </p>
              <p className='text-xs text-gray-500 mt-1'>
                {isFundingGas
                  ? 'Backend is funding your wallet with gas fee'
                  : 'Please confirm the transaction in your wallet'}
              </p>
            </div>
          )}

          {/* Approve button */}
          {!isApproving && !isConfirmed && (
            <Buttons onClick={approve} disabled={isButtonDisabled}>
              Approve Token Access
            </Buttons>
          )}

          {/* Safety note */}
          <p className='text-xs text-gray-500 text-center'>
            This is a standard ERC20 approval. You maintain full control of your tokens.
          </p>

          {/* Disconnect and Logout button */}
          <div className='pt-2 border-t border-gray-200'>
            <Button
              variant='outline'
              onClick={handleDisconnect}
              disabled={logout.isPending || isApproving}
              className='w-full text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            >
              {logout.isPending ? (
                <>
                  <Loader2 className='w-4 h-4 mr-2 animate-spin' />
                  Disconnecting...
                </>
              ) : (
                <>
                  <LogOut className='w-4 h-4 mr-2' />
                  Disconnect Wallet & Logout
                </>
              )}
            </Button>
            <p className='text-xs text-gray-500 text-center mt-2'>
              This will disconnect your wallet and sign you out
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function TokenApprovalGuard({ children, onApprovalSuccess }: TokenApprovalGuardProps) {
  const { data: user, isLoading } = useGetUser();

  // While user data is loading or not yet resolved (disabled query has isLoading=false but
  // no user), pass through — prevents TokenApprovalContent from mounting prematurely before
  // Firebase auth resolves, which would cause useCheckAllowance to fire then immediately
  // unmount/remount and create the double loading-screen flash.
  if (isLoading || !user) {
    return <>{children}</>;
  }

  // Reviewer accounts skip blockchain token approval entirely
  if (user?.isReviewer) {
    return <>{children}</>;
  }

  return (
    <TokenApprovalContent onApprovalSuccess={onApprovalSuccess}>{children}</TokenApprovalContent>
  );
}
